import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { Box, Paper } from "@mui/material";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import ticketScheme from "./TicketValidationScheme";
import {
  AttachmentUpload,
  createNewTicketThunk,
} from "../../modules/ticket/ticketForm/ticketFormOperations";
import { showNotificationMessage } from "../../modules/uiState";
import { route as ticketsListRoute } from "../TicketListContainer/TicketListScreen";
import { selectCurrentUser } from "../../modules/user/userActions";
import { currentEstateSelectors } from "../../modules/estate/currentEstate/currentEstateSlice";
import TicketFormHeader from "./TicketFormHeader";
import DocumentLayout from "../../components/Layouts/DocumentLayout";
import { useAppDispatch, useAppSelector } from "@/store/store";
import { useTicketFormSteps } from "./useTicketFormSteps";
import FormNavigation from "./FormNavigation";
import { buildTicketPayload } from "./buildTicketPayload";

export interface TicketFormValues {
  estateId: number | undefined;
  subject: string;
  body: string;
  place: string;
  serviceType: number | undefined;
  capture: AttachmentUpload[];
  anonymous: boolean;
  private: boolean;
}

/**
 * Ticket form component using React hooks instead of HOCs
 */
const TicketForm = () => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentEstate = useAppSelector(
    currentEstateSelectors.selectCurrentEstate,
  );

  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const next = (ev) => {
    if (ev?.preventDefault) ev.preventDefault();
    setCurrentStepIndex((index) => Math.min(index + 1, 2));
  };

  const previous = (ev) => {
    if (ev?.preventDefault) ev.preventDefault();
    setCurrentStepIndex((index) => Math.max(index - 1, 0));
  };

  const formik = useFormik({
    initialValues: {
      estateId: currentEstate?.id || undefined,
      subject: "",
      body: "",
      place: "",
      serviceType: undefined,
      capture: [],
      anonymous: false,
      private: true,
    },
    validateOnChange: false,
    validateOnBlur: true,
    validateOnMount: false,
    validationSchema: ticketScheme,
    onSubmit: (values: TicketFormValues, { resetForm, setSubmitting }) => {
      const ticket = buildTicketPayload(values, currentUser);
      dispatch(createNewTicketThunk(ticket))
        .then(() => {
          resetForm();
          dispatch(
            showNotificationMessage(
              intl.formatMessage({ id: "ticket.create.submit.success" }),
            ),
          );
          history.push(ticketsListRoute);
        })
        .catch((err) => {
          console.error(err);
          dispatch(
            showNotificationMessage(
              intl.formatMessage({ id: "ticket.create.submit.error" }),
            ),
          );
        })
        .finally(() => setSubmitting(false));
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    isValid,
    dirty,
    setFieldValue,
    validateField,
  } = formik;

  const serviceTypes = (currentEstate && currentEstate.serviceTypes) || [];

  // Define form steps
  const steps = useTicketFormSteps({
    serviceTypes,
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateField,
    setFieldValue,
    onNext: next,
  });

  const currentStep = steps[currentStepIndex];
  const CurrentStepComponent = currentStep.component;

  // Keep estateId in sync with currentEstate when available
  useEffect(() => {
    if (currentEstate?.id) {
      setFieldValue("estateId", currentEstate.id);
    }
  }, [currentEstate, setFieldValue]);

  return (
    <DocumentLayout>
      <Paper sx={{ py: 3 }}>
        <form
          onSubmit={handleSubmit}
          autoComplete="off"
          encType="multipart/form-data"
        >
          <DocumentLayout>
            <Box sx={{ mb: 2 }}>
              <TicketFormHeader
                page={currentStepIndex + 1}
                values={values}
                serviceTypes={serviceTypes}
              />
            </Box>
          </DocumentLayout>

          {/* Render current step */}
          <CurrentStepComponent {...currentStep.props} />

          {/* Navigation buttons */}
          {currentStep.showNavigation && (
            <DocumentLayout>
              <FormNavigation
                currentStep={currentStepIndex}
                totalSteps={steps.length}
                onNext={next}
                onPrevious={previous}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                canProceed={isValid && dirty}
                validationError={
                  errors.subject
                    ? intl.formatMessage({
                        id: "ticket.create.detail.error.wrong_subject",
                      })
                    : null
                }
              />
            </DocumentLayout>
          )}
        </form>
      </Paper>
    </DocumentLayout>
  );
};

export default TicketForm;
